// Configuration for conditional field requirements
// This structure defines which fields are required based on parent field values
const CONDITIONAL_FIELD_REQUIREMENTS = {
    // Health conditionals
    'prequal.tobacco': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [
            {name: 'tobaccoUsage', path: 'prequal.tobaccoUsage'},
            {name: 'tobaccoUsageDate', path: 'prequal.tobaccoUsageDate'},
            {name: 'tobaccoMethod', path: 'prequal.tobaccoMethod'},
        ],
    },

    'health.SleepApnea': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'sleepapneaSeverity', path: 'health.sleepapneaSeverity'}],
    },

    'health.Cancer': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'cancerStage', path: 'health.cancerStage'}],
    },

    // Behavior conditionals
    'prequal.DrugorAlcoholDependenceWithdrawal': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [
            {
                name: 'DrugorAlcoholDependenceWithdrawalTreatmentStatus',
                path: 'prequal.DrugorAlcoholDependenceWithdrawalTreatmentStatus',
            },
            {
                name: 'DrugorAlcoholDependenceWithdrawalTreatmentDate',
                path: 'prequal.DrugorAlcoholDependenceWithdrawalTreatmentDate',
            },
        ],
    },

    'prequal.DUI': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'DUIDate', path: 'prequal.DUIDate'}],
    },

    'prequal.License': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'suspendedLicense', path: 'prequal.suspendedLicense'}],
    },

    'prequal.Criminaloffense': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [
            {name: 'criminalOffenseDate', path: 'prequal.criminalOffenseDate'},
            {name: 'criminalOffenseChargeType', path: 'prequal.criminalOffenseChargeType'},
            {name: 'criminalOffenseProbation', path: 'prequal.criminalOffenseProbation'},
        ],
    },

    'prequal.Bankruptcy': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'bankruptcyOpen', path: 'prequal.bankruptcyOpen'}],
    },

    // Finance conditionals
    'prequal.isCurrentlyMilitary': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [{name: 'currentDeploymentOrders', path: 'prequal.currentDeploymentOrders'}],
    },

    'prequal.existingCoverage': {
        condition: (value: any) => value === true || value === 'true',
        requiredFields: [
            {name: 'existingPolicyType', path: 'prequal.existingPolicyType'},
            {name: 'existingCoverageAmount', path: 'prequal.existingCoverageAmount'},
            {name: 'replacingExistingPolicy', path: 'prequal.replacingExistingPolicy'},
        ],
    },
};

// Helper to get a value from a nested path
const getFieldValue = (store: any, path: string): any => {
    const parts = path.split('.');
    let current = store;
    for (const part of parts) {
        current = current?.[part];
        if (current === undefined) return undefined;
    }
    return current?.value;
};

// Helper function to check if Pre-Qualification fields are complete
export const checkPreQualFieldsComplete = (parameterStore: any): boolean => {
    const missingFields = getPreQualMissingFields(parameterStore);
    return missingFields.length === 0;
};

// Helper function to get the list of missing Pre-Qualification fields
export const getPreQualMissingFields = (parameterStore: any): Array<{name: string; path: string}> => {
    // Always required fields (only those with asterisks in the UI)
    const baseRequiredFields = [
        // Basic Information (from approval section for names)
        {name: 'state', path: 'prequal.state'},
        {name: 'gender', path: 'prequal.gender'},
        {name: 'age', path: 'prequal.age'},

        // Health section required fields
        {name: 'tobacco', path: 'prequal.tobacco'},
        {name: 'height', path: 'prequal.height'},
        {name: 'weight', path: 'prequal.weight'},

        // Health conditions that are always required
        {name: 'WeightLossObesityGastric', path: 'health.WeightLossObesityGastric'},
        {name: 'SleepApnea', path: 'health.SleepApnea'},
        {name: 'Cancer', path: 'health.Cancer'},
        {name: 'HIVAIDS', path: 'health.HIVAIDS'},
        {name: 'AtrialFibulationAfib', path: 'health.AtrialFibulationAfib'},
        {name: 'BipolarDisorder', path: 'health.BipolarDisorder'},
        {name: 'Schizophrenia', path: 'health.Schizophrenia'},
        {name: 'Defibrillator', path: 'health.Defibrillator'},
        {name: 'Transplant', path: 'health.Transplant'},

        // Behavior section required fields
        {name: 'DrugorAlcoholDependenceWithdrawal', path: 'prequal.DrugorAlcoholDependenceWithdrawal'},
        {name: 'DUI', path: 'prequal.DUI'},
        {name: 'License', path: 'prequal.License'},
        {name: 'Criminaloffense', path: 'prequal.Criminaloffense'},
        {name: 'Bankruptcy', path: 'prequal.Bankruptcy'},

        // Finances section required fields
        {name: 'incomeHouseholdText', path: 'prequal.incomeHouseholdText'},
        {name: 'existingCoverage', path: 'prequal.existingCoverage'},
        {name: 'isCurrentlyMilitary', path: 'prequal.isCurrentlyMilitary'},

        // NOT REQUIRED (no asterisk in UI):
        // - kids (Children)
        // - maritalStatus
        // - currentlyEmployed (Employment status)
        // - intentFocus (Main Focus question)
        // - intentFunding (Funding preference question)
    ];

    // Build the complete list of required fields
    const allRequiredFields = [...baseRequiredFields];

    // Add conditional fields based on the configuration
    Object.entries(CONDITIONAL_FIELD_REQUIREMENTS).forEach(([parentPath, config]) => {
        const parentValue = getFieldValue(parameterStore, parentPath);
        if (config.condition(parentValue)) {
            allRequiredFields.push(...config.requiredFields);
        }
    });

    // Check missing fields
    const missingFields = allRequiredFields.filter((fieldConfig) => {
        const value = getFieldValue(parameterStore, fieldConfig.path);
        const hasValue = value !== null && value !== undefined && value !== '';
        return !hasValue;
    });

    // Log missing fields if any exist
    //if (missingFields.length > 0) {
    //    console.log(
    //        'Pre-Qualification validation failed. Missing fields:',
    //        missingFields.map((field) => ({
    //            name: field.name,
    //            path: field.path,
    //        })),
    //    );
    //}

    return missingFields;
};
